import React, { useState } from "react";
import { ChevronDown, Bookmark, X, Calendar, MapPin } from "lucide-react";
import TierSelector, { parseMultiTier, serializeMultiTier, multiTierDisplay } from "./TierSelector";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import { isoToDate, dateToISO } from "../../utils/friendlyDates";

// Friendlies browse filter bar. Chips open popovers (Last-Minute FilterPopover
// pattern); active selections also render as removable chips with a "Clear all".
// Filters map 1:1 to the M1 feed params: date_from/date_to, country, team_category,
// tier (single). The Saved chip toggles the ?saved_only view with its count.

export const TEAM_CATEGORY_CHOICES = [
  { value: "first_team", label: "First team" },
  { value: "first_team_youth", label: "First team + Youth" },
  { value: "youth_2nd", label: "Youth · 2nd team" },
];

const teamLabel = (v) => TEAM_CATEGORY_CHOICES.find((c) => c.value === v)?.label || "";

const FilterPopover = ({ label, active, children, width = 260 }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${
          active ? "border-[#2283FF] bg-[#F0F7FF] text-[#1466D8]" : "border-[#E4E9F1] text-[#5A6B82]"
        }`}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 z-20 mt-1 bg-white border border-[#E4E9F1] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)] p-3"
            style={{ width }}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};

const ActiveChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF3FF] text-[#1466D8] px-2.5 py-1 text-[12px] font-semibold">
    {label}
    <button onClick={onRemove} aria-label={`Remove ${label}`} className="hover:text-[#0F4EA8]">
      <X className="w-3.5 h-3.5" />
    </button>
  </span>
);

const FriendlyFilterBar = ({
  filters,
  setFilter,
  setDateRange,
  removeFilter,
  clearFilters,
  savedOnly,
  toggleSavedOnly,
  savedCount = 0,
  countries = [],
  count = 0,
  hasActiveFilters,
}) => {
  const [countryQuery, setCountryQuery] = useState("");
  const tierVal = parseMultiTier(filters.tier);
  const tierActive = !!filters.tier;
  const dateActive = !!(filters.date_from || filters.date_to);
  const dateLabel = dateActive
    ? [filters.date_from, filters.date_to].filter(Boolean).join(" – ")
    : "Date range";

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {/* Date range */}
        <FilterPopover
          label={<span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{dateLabel}</span>}
          active={dateActive}
        >
          <p className="text-[11.5px] text-[#8A98AC] mb-2">Show friendlies within a date window.</p>
          <div className="flex flex-col gap-2">
            <CustomDateTimePicker value={isoToDate(filters.date_from)} onChange={(d) => setDateRange(dateToISO(d), filters.date_to)} placeholder="From" dateFormat="dd MMM yyyy" />
            <CustomDateTimePicker value={isoToDate(filters.date_to)} onChange={(d) => setDateRange(filters.date_from, dateToISO(d))} placeholder="To" dateFormat="dd MMM yyyy" minDate={isoToDate(filters.date_from)} />
          </div>
          {dateActive && (
            <button onClick={() => removeFilter("date")} className="mt-2 text-[12px] text-[#C4402F] hover:underline">
              Clear dates
            </button>
          )}
        </FilterPopover>

        {/* Location (country). Camp requests match their DESTINATION server-side. */}
        <FilterPopover
          label={<span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{filters.country ? filters.country : "Location"}</span>}
          active={!!filters.country}
        >
          <input
            value={countryQuery}
            onChange={(e) => setCountryQuery(e.target.value)}
            placeholder="Search countries…"
            className="w-full bg-[#F3F4F6] rounded-md px-2.5 py-1.5 text-[13px] mb-2"
          />
          <div className="max-h-[200px] overflow-y-auto">
            {filters.country && (
              <button onClick={() => setFilter("country", "")} className="w-full text-left px-2 py-1.5 rounded-md text-[13px] text-[#C4402F] hover:bg-gray-50">
                Clear ({filters.country})
              </button>
            )}
            {countries
              .filter((c) => (countryQuery ? (c.name || "").toLowerCase().includes(countryQuery.toLowerCase()) : true))
              .slice(0, 40)
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => setFilter("country", c.name)}
                  className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md text-[13px] ${
                    filters.country === c.name ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : "hover:bg-gray-50"
                  }`}
                >
                  {c.image_url && <img src={c.image_url} alt="" className="w-4 h-3 object-contain" referrerPolicy="no-referrer" />}
                  {c.name}
                </button>
              ))}
          </div>
          <p className="mt-2 text-[11px] text-[#98A2B3]">Falls back to country &amp; region. The platform has no per-club coordinates.</p>
        </FilterPopover>

        {/* Team category (the 3 structured categories) */}
        <FilterPopover label={filters.team_category ? `Team · ${teamLabel(filters.team_category)}` : "Team · All"} active={!!filters.team_category} width={220}>
          <div className="flex flex-col">
            <button
              onClick={() => setFilter("team_category", "")}
              className={`text-left px-2 py-1.5 rounded-md text-[13px] ${!filters.team_category ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : "hover:bg-gray-50"}`}
            >
              All teams
            </button>
            {TEAM_CATEGORY_CHOICES.map((c) => (
              <button
                key={c.value}
                onClick={() => setFilter("team_category", c.value)}
                className={`text-left px-2 py-1.5 rounded-md text-[13px] ${filters.team_category === c.value ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : "hover:bg-gray-50"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </FilterPopover>

        {/* Opponent level — full multi-select (BE ?tier= accepts 1 / 1,2 / 3_or_lower / any). */}
        <FilterPopover label={tierActive ? `Opponent · ${multiTierDisplay(tierVal)}` : "Opponent level · Any"} active={tierActive} width={280}>
          <p className="text-[11.5px] text-[#8A98AC] mb-2">Pick the tiers you'd play. Multi-select for a range.</p>
          <TierSelector
            mode="multi"
            value={tierVal}
            onChange={(v) => setFilter("tier", serializeMultiTier(v))}
            showThreeOrLower
            showAny
          />
        </FilterPopover>

        {/* Saved view toggle */}
        <button
          onClick={toggleSavedOnly}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${
            savedOnly ? "border-[#2283FF] bg-[#F0F7FF] text-[#1466D8]" : "border-[#E4E9F1] text-[#5A6B82]"
          }`}
          aria-pressed={savedOnly}
        >
          <Bookmark className="w-3.5 h-3.5" style={savedOnly ? { fill: "#2283FF", color: "#2283FF" } : undefined} />
          Saved {savedCount > 0 && <span className="text-[11px]">[{savedCount}]</span>}
        </button>

        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-[12px] font-semibold text-[#2283FF] hover:underline">
            Reset
          </button>
        )}

        <span className="ml-auto text-[12.5px] text-[#8A98AC]">{count} open {count === 1 ? "request" : "requests"}</span>
      </div>

      {/* Active-filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {dateActive && <ActiveChip label={dateLabel} onRemove={() => removeFilter("date")} />}
          {filters.country && <ActiveChip label={filters.country} onRemove={() => removeFilter("country")} />}
          {filters.team_category && <ActiveChip label={teamLabel(filters.team_category)} onRemove={() => removeFilter("team_category")} />}
          {tierActive && <ActiveChip label={multiTierDisplay(tierVal)} onRemove={() => removeFilter("tier")} />}
          {savedOnly && <ActiveChip label="Saved" onRemove={toggleSavedOnly} />}
          <button onClick={clearFilters} className="text-[12px] font-semibold text-[#5A6B82] hover:text-[#2283FF]">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default FriendlyFilterBar;
